import React from 'react';

import { useSelector } from 'react-redux';

import { StatsigFeatureKeys } from 'src/features';
import { useStatsigGate } from 'src/hooks/statsig';
import { Site } from 'src/redux/jira/types';
import { getJiraConnectionsCreatedAt } from 'src/selectors/global-selectors';
import { urls as jiraUrls } from 'src/urls/jira';
import { captureExceptionWithTags } from 'src/utils/sentry';

const HOURS_UNTIL_AVAILABLE = 24;
const MINUTES_UNTIL_AVAILABLE = HOURS_UNTIL_AVAILABLE * 60;
const PROCESSING_ROVO_CHAT_DATA_WAIT_MS = MINUTES_UNTIL_AVAILABLE * 60 * 1000;

const isWithinRovoDataProcessingWaitTime = (
  connectedAtTime: number
): boolean => {
  const elapsedMsSinceConnection = Date.now() - connectedAtTime;
  return elapsedMsSinceConnection < PROCESSING_ROVO_CHAT_DATA_WAIT_MS;
};

const filterRecentlyConnectedSites = (
  sites: Site[],
  jiraConnectionsCreatedAt: string
) => {
  // Filter out sites that have been connected in the last 24hrs.
  try {
    const siteToTimestampMap = JSON.parse(jiraConnectionsCreatedAt);
    const filteredSites = sites.filter(site => {
      const connectedAtTime = siteToTimestampMap[site.cloudId];
      if (
        Number.isInteger(connectedAtTime) &&
        isWithinRovoDataProcessingWaitTime(connectedAtTime)
      ) {
        return false;
      }
      return true;
    });
    return filteredSites;
  } catch (e) {
    // Keep going if jiraConnectionsCreatedAt is corrupted.
    captureExceptionWithTags(e, {
      feature: 'bbc-rovo-data-onboarding-ux',
      component: 'bbc-rovo-chat',
    });
    return sites;
  }
};

export const useRovoEnabledSitesForWorkspace = (
  workspaceUuid: string,
  isJiraSitesCallEnabled: boolean = false
) => {
  const isRovoOnboardingUXEnabled = useStatsigGate(
    StatsigFeatureKeys.isRovoOnboardingUXEnabled
  );
  const jiraConnectionsCreatedAt = useSelector(getJiraConnectionsCreatedAt);
  const [rovoEnabledSites, setRovoEnabledSites] = React.useState<Site[]>([]);

  React.useEffect(() => {
    if (!workspaceUuid || !isJiraSitesCallEnabled) {
      return;
    }

    const url = jiraUrls.api.internal.aiConnectedSites(workspaceUuid);

    fetch(url)
      .then(res => {
        res.json().then((data: { values: Site[] }) => {
          const sites = data.values.filter(site => site.isRovoLLMEnabled);
          if (isRovoOnboardingUXEnabled && jiraConnectionsCreatedAt) {
            const filteredSites = filterRecentlyConnectedSites(
              sites,
              jiraConnectionsCreatedAt
            );
            setRovoEnabledSites(filteredSites);
          } else {
            setRovoEnabledSites(sites);
          }

          return data;
        });
      })
      .catch(error => {
        captureExceptionWithTags(error, {
          component: 'bbc-rovo-chat',
        });
      });
  }, [
    isJiraSitesCallEnabled,
    isRovoOnboardingUXEnabled,
    jiraConnectionsCreatedAt,
    workspaceUuid,
  ]);

  return { rovoEnabledSites };
};
